import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { EnumViewModel } from "app/api/models";
import { UploadFilePopupComponent } from "app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component";

export class AddItemHideConfig {
    property: string;
    value: any;
}

export class AddItemValueChangeConfig {
    property: string;
    value: any;
}

export interface EnumViewModelWithInfo {
    name?: null | string;
    value?: number | string | null;
    isHeader?: boolean;
    info?: null | string;
    tabName?: null | string; //for tabbed selects
}

export class AddItemData {
    name: string;
    icon?: string;
    initialValue?: string;
    type?: string;
    propertyName?: string;
    mandatory?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    options?: EnumViewModelWithInfo[]; //for select and autocomplete type only
    colSpan?: number;
    hideConfig?: AddItemHideConfig;
    valueConfig?: AddItemValueChangeConfig;
    hasTabs?: boolean; //for radio/radioMulti - split lists into tabs by item tabName
    hide?: boolean;
}

export class AddButtonData {
    name: string;
    closeDialog: boolean;
    closeWithFormValues: boolean = false;
    color?: string = null;
    isOnRight: boolean = false;
    class?: string = '';
    callback: () => void;
}

@Component({
    selector       : 'add-item-dialog',
    templateUrl    : './add-item-dialog.component.html'
})
export class AddItemDialogComponent implements OnInit
{
    public addItemForm: FormGroup;

    title: string;
    formConfig: AddItemData[];
    formValues: string[];

    showDeleteButton: boolean;
    overrideSaveText: string;
    overrideDeleteText: string;
    overrideSaveClass: string;
    overrideWidth: number;
    additionalHtml: string;
    additionalPreHtml: string;

    // files: File[] = [];

    constructor(
        public dialogRef: MatDialogRef<AddItemDialogComponent>,
        private _formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: {title: string, items: AddItemData[], showDeleteButton: boolean, overrideSaveText: string, overrideSaveClass: string, overrideDeleteText: string, overrideWidth: number, buttons: AddButtonData[], additionalHtml: string, additionalPreHtml: string,}
    )
    {}

    ngOnInit(): void
    {
        this.title = this.data.title;
        this.formConfig = this.data.items;
        this.showDeleteButton = this.data.showDeleteButton;
        this.overrideSaveText = this.data.overrideSaveText ?? 'Save';
        this.overrideDeleteText = this.data.overrideDeleteText ?? 'Delete';
        this.overrideSaveClass = this.data.overrideSaveClass ?? '';
        this.overrideWidth = this.data.overrideWidth ?? 25;
        this.additionalHtml = this.data.additionalHtml;
        this.additionalPreHtml = this.data.additionalPreHtml;
        
        //set tabs if applicable
        for(var configItem of this.formConfig.filter(x => x.hasTabs))
            configItem['selectedTab'] = 0;//this.getTabNames(config)[0];

        this.buildForm();
    }

    buildForm() {
        var group = {};

        this.formConfig.forEach(item => {
            var name = this.getFormControlName(item);

            // if(item.type == 'files')
            //     this.files.push(...(<any>item.initialValue || []));

            if(item.type == 'heading') return;

            group[name] = [item.initialValue === undefined ? '' : item.initialValue];

            if(item.type == 'autocomplete')
                item['filteredOptions'] = item.options.map(x => x.name);
        });

        this.addItemForm = this._formBuilder.group(group);

        this.formConfig.filter(x => x.mandatory).forEach(item => this.addItemForm.controls[this.getFormControlName(item)].addValidators(Validators.required));
        this.formConfig.filter(x => x.disabled).forEach(item => this.addItemForm.controls[this.getFormControlName(item)].disable());

        this.cdr.detectChanges();
    }

    getFormControlName(item: AddItemData) {
        if(!item) return null;

        return item.propertyName ?? item.name;
    }

    isHideConfig(configItem: AddItemData) {
        var hideConfig = configItem?.hideConfig;
        if(!hideConfig) return false;

        return this.addItemForm.controls[hideConfig.property].value == hideConfig.value;
    }

    selectHasHeaders = (configItem: AddItemData) => configItem.options.some(x => x.isHeader);

    addToRadioMulti(value: any, controlName: string) {
        var radioItems = this.addItemForm.controls[controlName].value ?? [];
        radioItems.push(value);
        this.addItemForm.controls[controlName].setValue(radioItems);
        this.addItemForm.markAsDirty();
    }

    removeRadioMultiChip(index: any, controlName: string) {
        var radioItems = this.addItemForm.controls[controlName].value ?? [];
        radioItems = radioItems.filter((_, i) => i != index);
        
        this.addItemForm.controls[controlName].setValue(radioItems);
        this.addItemForm.markAsDirty();
    }

    getOptionName(options: EnumViewModelWithInfo[], value: number | string) {
        return options.find(x => x.value == value)?.name;
    }
    
    filterAutocomplete(event: any, item: AddItemData) {
        var text = event.target.value;

        item['filteredOptions'] = item.options.filter(m => m.name.toLowerCase().includes(text.toLowerCase())).map(x => x.name);
        this.cdr.detectChanges();
    }
    
    save() {
        this.dialogRef.close(this.addItemForm.value);
    }

    close() {
        this.dialogRef.close(null);
    }

    deleteItem() {
        this.dialogRef.close({delete: true});
    }

    doButtonCallback(button: AddButtonData) {
        button.callback();

        if(button.closeDialog) {
            if(button.closeWithFormValues)
                this.save();
            else 
                this.close();
        }
    }

    anyOptionsHaveInfo = (configItem: AddItemData) => configItem.options?.filter(x => !!x.info).length > 0;

    getTabNames = (configItem: AddItemData) => configItem.options.map(x => x.tabName).filter((val, i, arr) => arr.indexOf(val) === i);

    getRadioOptions(configItem: AddItemData) {
        if(!configItem.hasTabs)
            return configItem.options;

        var currentTab = this.getTabNames(configItem)[configItem['selectedTab']];
        
        return configItem.options.filter(x => x.tabName == currentTab);
    }


    // Files
    getFiles(controlName: string) : File[] {
        return this.addItemForm.controls[controlName].value;
    }

    openFileUpload(controlName: string) {
        var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            var files = this.getFiles(controlName);
            
            for(var file of dialogRes) {
                file['extension'] = '.' + file.name.split('.').pop();
                files.push(file);
            }
            
            console.log(files);
            this.addItemForm.controls[controlName].setValue(files);
            console.log("form value", this.getFiles(controlName));
        });    
    }

    deleteFile(file: File, controlName: string) {
        var files = this.getFiles(controlName);
        this.addItemForm.controls[controlName].setValue(files.filter(x => x.name != file.name));
    }
}